import { HelpCircleIcon } from "lucide-react";
import * as React from "react";

import { SimpleTooltip } from "@/DesignSystem/nanny/SimpleTooltip/SimpleTooltip";
import { ExperimentStatus } from "@/apis/nannyml";
import { FailedIcon, SuccessIcon } from "@/components/Icons";
import { experimentStatusLabels } from "@/formatters/experiment";
import { cn } from "@/lib/utils";

const icons: Record<ExperimentStatus, JSX.Element> = {
  [ExperimentStatus.Accepted]: <SuccessIcon />,
  [ExperimentStatus.Rejected]: <FailedIcon />,
  [ExperimentStatus.Ongoing]: <HelpCircleIcon className="text-running" size={20} strokeWidth={1.5} />,
};

const colors: Record<ExperimentStatus, string> = {
  [ExperimentStatus.Accepted]: "text-success",
  [ExperimentStatus.Rejected]: "text-failed",
  [ExperimentStatus.Ongoing]: "text-running",
};

export const ExperimentStatusIcon = ({ status, tooltip = true }: { status: ExperimentStatus; tooltip?: boolean }) =>
  tooltip ? (
    <SimpleTooltip tooltipContent={<ExperimentStatusLabel status={status} />} side="right">
      {icons[status]}
    </SimpleTooltip>
  ) : (
    icons[status]
  );

export const ExperimentStatusLabel = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & {
    status: ExperimentStatus;
  }
>(({ status, className }, ref) => (
  <span ref={ref} className={cn(colors[status], className)}>
    {experimentStatusLabels[status]}
  </span>
));

export const ExperimentStatusSummary = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & {
    statuses: ExperimentStatus[];
  }
>(({ className, statuses, ...props }, ref) => (
  <span ref={ref} className={cn("inline-flex gap-3", className)} {...props}>
    {statuses.some((status) => status === ExperimentStatus.Accepted) && (
      <ExperimentStatusIcon status={ExperimentStatus.Accepted} tooltip={false} />
    )}
    {statuses.some((status) => status === ExperimentStatus.Rejected) && (
      <ExperimentStatusIcon status={ExperimentStatus.Rejected} tooltip={false} />
    )}
    {statuses.some((status) => status === ExperimentStatus.Ongoing) && (
      <ExperimentStatusIcon status={ExperimentStatus.Ongoing} tooltip={false} />
    )}
  </span>
));
